import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { TableShell } from '../../components/molecules/TableShell'
import { Th, Td } from '../../components/molecules/Table'
import { WebhookProviderEditModal } from './WebhookProviderEditModal'
import { WebhookProviderSecretModal } from './WebhookProviderSecretModal'
import {
  useWebhookProviders,
  type WebhookProviderDto
} from '../../../viewmodels/useWebhookProviders'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /preferences/webhook-providers — configure per-provider event-name →
 * OrderStatus mappings + HMAC signing secrets for external integrations
 * (Shiprocket, Delhivery, Razorpay, …). Kiran 2026-06-18.
 *
 * Both the mappings AND the secrets are edited here per Kiran's call —
 * secrets are write-only from the UI (we show fingerprints + can rotate,
 * never reveal the raw value).
 */
export function WebhookProvidersPage(): JSX.Element {
  const vm = useWebhookProviders()
  const { can } = useAuth()
  const canAdd = can(SCREENCODES.PREFERENCESWEBHOOKPROVIDERS, 'add')
  const canEdit = can(SCREENCODES.PREFERENCESWEBHOOKPROVIDERS, 'edit')
  const canDelete = can(SCREENCODES.PREFERENCESWEBHOOKPROVIDERS, 'delete')

  const [editTarget, setEditTarget] = useState<WebhookProviderDto | 'new' | null>(null)
  const [secretTarget, setSecretTarget] = useState<WebhookProviderDto | null>(null)
  const [pendingDelete, setPendingDelete] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const handleDelete = async (provider: WebhookProviderDto): Promise<void> => {
    if (!window.confirm(`Delete provider "${provider.display_name}"? Its event map AND HMAC secret are removed.`)) {
      return
    }
    setActionError(null)
    setPendingDelete(provider.provider)
    const res = await vm.remove(provider.provider)
    setPendingDelete(null)
    if (!res.ok) setActionError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-h2 text-charcoal">Webhook providers</h1>
          <p className="font-body text-body-sm text-text-secondary mt-1">
            External integrations (shipping carriers, payment gateways) that POST
            order-status events to the storefront. Per-provider event-name →
            OrderStatus mapping + HMAC signing secret.
          </p>
        </div>
        {canAdd ? <Button onClick={() => setEditTarget('new')}>Add provider</Button> : null}
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={vm.reload}
          >
            Retry
          </button>
        </Alert>
      ) : null}
      {actionError !== null ? <Alert tone="error">{actionError}</Alert> : null}

      {vm.isLoading && vm.items.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : vm.items.length === 0 ? (
        <Placeholder>
          No providers configured yet. Add one to start mapping external events to
          OrderStatus.
        </Placeholder>
      ) : (
        <TableShell>
          <table className="w-full font-body text-body-sm">
            <thead className="bg-parchment/50 text-text-secondary">
              <tr>
                <Th>Provider</Th>
                <Th>Display name</Th>
                <Th className="text-right">Mapped events</Th>
                <Th>Signing secret</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {vm.items.map((p) => (
                <tr key={p.provider} className="border-t border-border align-middle">
                  <Td className="font-mono text-body-xs">{p.provider}</Td>
                  <Td className="text-charcoal">{p.display_name}</Td>
                  <Td className="text-right font-mono">{Object.keys(p.events).length}</Td>
                  <Td>
                    {p.has_secret ? (
                      <span className="font-mono text-body-xs text-text-secondary">
                        {p.secret_fingerprint}
                      </span>
                    ) : (
                      <span className="text-body-xs text-rust">— not set —</span>
                    )}
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    {canEdit ? (
                      <>
                        <Button size="sm" variant="secondary" onClick={() => setEditTarget(p)}>
                          Edit
                        </Button>{' '}
                        <Button size="sm" variant="secondary" onClick={() => setSecretTarget(p)}>
                          {p.has_secret ? 'Rotate secret' : 'Set secret'}
                        </Button>
                      </>
                    ) : null}
                    {canDelete ? (
                      <>
                        {' '}
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={pendingDelete === p.provider}
                          onClick={() => void handleDelete(p)}
                        >
                          {pendingDelete === p.provider ? 'Deleting…' : 'Delete'}
                        </Button>
                      </>
                    ) : null}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}

      {editTarget !== null ? (
        <WebhookProviderEditModal
          open={editTarget !== null}
          initial={editTarget === 'new' ? null : editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={async (input) => {
            if (editTarget === 'new') {
              return vm.create({
                provider: input.provider,
                display_name: input.display_name,
                events: input.events
              })
            }
            return vm.update(editTarget.provider, {
              display_name: input.display_name,
              events: input.events
            })
          }}
        />
      ) : null}

      {secretTarget !== null ? (
        <WebhookProviderSecretModal
          open={secretTarget !== null}
          provider={secretTarget}
          onClose={() => setSecretTarget(null)}
          onSubmit={async (value) => vm.rotateSecret(secretTarget.provider, value)}
        />
      ) : null}
    </div>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}
